import React, { useEffect, useState } from 'react';
import './localHiderLanding.css';

const SUPPORT_URL = 'https://mindware.com.mx/support';
const WAITLIST_URL = 'mailto:contact@mindware.com.mx?subject=Local%20Hider%20-%20Early%20Access';

const FEATURES = [
  { icon: 'fa-map-marker', title: 'Draw Your Own Turf', desc: 'Tap the map to chalk out the play area, drop landmarks, and set the Seeker start point. Any neighborhood becomes a board.' },
  { icon: 'fa-clock-o', title: 'Head Start Timer', desc: 'The Hider gets minutes to disappear while everyone else waits at the starting point, eyes closed.' },
  { icon: 'fa-id-card-o', title: 'Clue Cards', desc: 'Seekers spend cards to ask Radar, Thermometer, or Distance questions. Every answer can hand the Hider a curse right back.' },
  { icon: 'fa-qrcode', title: 'One Code, Instant Match', desc: 'Share a 4-character code or scan a QR. Two players is enough to start — no accounts, no setup.' },
  { icon: 'fa-map-o', title: 'Live GPS Tracking', desc: 'Boundaries, landmarks, and player pings update in real time on the match map, down to a few meters.' },
  { icon: 'fa-trophy', title: 'Stats & Leaderboards', desc: 'Matches, hidden time, distance covered — everyone takes a turn hiding, and the longest time wins.' },
];

const STEPS = [
  { n: '01', title: 'Create or join a match', desc: 'Start a new game or hop in with a code or QR scan. Two players is enough to play.' },
  { n: '02', title: 'Draw the play area', desc: 'Mark the boundary, the Seeker start point, and a few landmarks right on the map.' },
  { n: '03', title: 'Ten minutes to disappear', desc: 'The Hider gets a head start while Seekers wait at the starting point.' },
  { n: '04', title: 'Buy clues, ask questions', desc: 'Radar, Thermometer, Distance — every answer costs the Hider time or hands them a curse.' },
  { n: '05', title: 'Longest hidden wins', desc: 'Everyone takes a turn as the Hider. Best time across the group takes the match.' },
];

const HomeScreen = () => (
  <div className="lh-screen lh-screen-home">
    <div className="lh-screen-topbar">
      <span className="lh-dot" />
      <span>Local Hider</span>
      <i className="fa fa-cog" />
    </div>
    <div className="lh-stat-row">
      <div className="lh-stat"><strong>0</strong><small>Matches</small></div>
      <div className="lh-stat"><strong>0 min</strong><small>Hidden time</small></div>
      <div className="lh-stat"><strong>0</strong><small>Yards</small></div>
    </div>
    <div className="lh-action-row">
      <div className="lh-action">
        <i className="fa fa-user-plus" />
        <span>Create match</span>
      </div>
      <div className="lh-action">
        <i className="fa fa-qrcode" />
        <span>Join match</span>
      </div>
    </div>
    <div className="lh-recent-label">RECENT MATCHES</div>
    <div className="lh-recent-empty">No games yet</div>
  </div>
);

const MapScreen = () => (
  <div className="lh-screen lh-screen-map">
    <div className="lh-map-timer">
      <small>TIME TO HIDE</small>
      <strong>09:57</strong>
    </div>
    <div className="lh-map-canvas">
      <svg viewBox="0 0 200 200" className="lh-map-boundary">
        <polygon points="60,20 140,35 170,110 120,175 40,150 25,80" />
      </svg>
      {[
        [60, 20], [140, 35], [170, 110], [120, 175], [40, 150], [25, 80],
      ].map((p, i) => (
        <span key={i} className="lh-map-node" style={{ left: `${p[0] / 2}%`, top: `${p[1] / 2}%` }}>{i + 1}</span>
      ))}
      <span className="lh-map-pin lh-map-seeker" style={{ left: '38%', top: '58%' }} />
      <span className="lh-map-pin lh-map-player" style={{ left: '55%', top: '42%' }} />
      <span className="lh-map-pin lh-map-player" style={{ left: '46%', top: '68%' }} />
    </div>
    <div className="lh-map-banner">No peeking yet! Stay at the starting point.</div>
  </div>
);

const PhoneMock = ({ screen }) => (
  <div className="lh-phone">
    <div className="lh-phone-notch" />
    <div className="lh-phone-screen">
      {screen === 'home' ? <HomeScreen /> : <MapScreen />}
    </div>
    <div className="lh-phone-home" />
  </div>
);

export const LocalHiderLanding = () => {
  const [activeScreen, setActiveScreen] = useState('home');

  useEffect(() => {
    const prev = document.title;
    document.title = 'Local Hider | Hide. Seek. Win.';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="lh-page">

      {/* ── Nav ── */}
      <nav className="lh-nav">
        <div className="lh-nav-inner">
          <a href="/local-hider" className="lh-nav-brand">
            <span className="lh-mark"><span /></span>
            <span>Local Hider</span>
          </a>
          <div className="lh-nav-links">
            <a href="#lh-how">How it Works</a>
            <a href="#lh-features">Features</a>
            <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">Support</a>
            <a href={WAITLIST_URL} className="lh-nav-cta">Get Early Access</a>
          </div>
          <a href="/" className="lh-back">&#8592; Mindware</a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="lh-hero">
        <div className="lh-hero-bg" />
        <div className="lh-wrap lh-hero-inner">
          <div className="lh-hero-copy">
            <span className="lh-eyebrow">Real-World Hide &amp; Seek</span>
            <h1>
              HIDE. SEEK.<br /><span className="lh-grad">WIN.</span>
            </h1>
            <p>
              Local Hider turns your neighborhood into a real-world hide-and-seek game.
              Draw the play area, hide with GPS, and let Seekers hunt you down with clue cards.
            </p>
            <div className="lh-hero-actions">
              <a href={WAITLIST_URL} className="lh-btn-primary">Get Early Access</a>
              <a href="#lh-how" className="lh-btn-ghost">See how it works &#8594;</a>
            </div>
            <div className="lh-store-badges">
              <div className="lh-store-badge is-soon">
                <i className="fa fa-apple" />
                <div><small>Coming soon to the</small><strong>App Store</strong></div>
              </div>
              <div className="lh-store-badge is-soon">
                <i className="fa fa-android" />
                <div><small>Coming soon on</small><strong>Google Play</strong></div>
              </div>
            </div>
            <p className="lh-credit">
              Inspired by the hide-and-seek format popularized by <em>Jet Lag: The Game</em>.
              Local Hider is an independent app, not affiliated with or endorsed by Jet Lag: The Game.
            </p>
          </div>
          <div className="lh-hero-phones">
            <div className="lh-phone-tabs">
              {[['home', 'Home'], ['map', 'Live Map']].map(([s, l]) => (
                <button
                  key={s}
                  className={`lh-phone-tab${activeScreen === s ? ' is-active' : ''}`}
                  onClick={() => setActiveScreen(s)}
                >
                  {l}
                </button>
              ))}
            </div>
            <PhoneMock screen={activeScreen} />
          </div>
        </div>
      </section>

      {/* ── How it Works ── */}
      <section id="lh-how" className="lh-section lh-how-section">
        <div className="lh-wrap">
          <div className="lh-section-head">
            <span className="lh-label">How it Works</span>
            <h2>One match, five moves</h2>
            <p>From lobby to hiding spot to bragging rights — here is a full round of Local Hider.</p>
          </div>
          <div className="lh-steps">
            {STEPS.map((s) => (
              <div key={s.n} className="lh-step">
                <span className="lh-step-n">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="lh-features" className="lh-section lh-features-section">
        <div className="lh-wrap">
          <div className="lh-section-head">
            <span className="lh-label">Features</span>
            <h2>Everything a Hider and a Seeker need</h2>
            <p>Built for real streets, real parks, and real head starts.</p>
          </div>
          <div className="lh-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="lh-feat-card">
                <i className={`fa ${f.icon} lh-feat-icon`} />
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="lh-cta" className="lh-section lh-cta-section">
        <div className="lh-wrap lh-cta-inner">
          <span className="lh-label">Get Started</span>
          <h2>Be the first to hide</h2>
          <p>
            Local Hider is in active development. Join the early access list and we will
            let you know the moment matches go live in your city.
          </p>
          <a href={WAITLIST_URL} className="lh-btn-primary lh-btn-lg">
            Get Early Access &nbsp;<i className="fa fa-arrow-right" />
          </a>
          <div className="lh-cta-sub">
            <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">Need help or have questions? Visit Support &#8594;</a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lh-footer">
        <div className="lh-wrap lh-footer-inner">
          <span>Local Hider by <a href="/">Mindware</a></span>
          <div className="lh-footer-links">
            <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">Support</a>
            <a href="/privacy_policy_local_hider">Privacy Policy</a>
            <a href="/">&#8592; Back to Mindware</a>
          </div>
        </div>
      </footer>

    </div>
  );
};
